import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import IconButton from '@material-ui/core/IconButton';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import ElectionDetails from 'app/components/election/ElectionDetails';
import ProposalDetails from 'app/components/election/ProposalDetails';
import FormBuilder from 'app/components/form/FormBuilder';
import RecommendationDetails from 'app/components/election/RecommendationDetails';
import VotingDetails from 'app/components/election/VotingDetails';
import OtherDetails from 'app/components/election/OtherDetails';
import ElectionReview from 'app/components/election/ElectionReview';

const steps = [
    'Election',
    'Proposals',
    'Proposal Form',
    'Recommendations',
    'Voting',
    'Other',
    'Review'
];

const useStyles = makeStyles(theme => ({
    input: {
        display: 'none',
    },
    listSubHeader: {
        backgroundColor: '#ffffff',
        color: theme.palette.error.main
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogPaper: {
        overflowY: 'initial'
    },
    height: {
        height: '100%'
    },
    content: {
        height: '100%',
        padding: '0'
    }
}));

const initElection = {
    proposal_group_id: 0,
    recommendation_group_id: 0,
    review_group_id: 0,
    voting_group_id: 0,
    results_group_id: 0,
    order_field_id: 0,
    name: ``,
    button_name: ``,
    description: ``,
    starts_at: null,
    repeated: false,
    repeat_period: 'years',
    repeat_amount: 1,
    add_date_to_name: 0, // 0 = no, 1 = beginning of title, 2 = end of title
    order_field_dir: false,
    proposals_active: false,
    proposals_start_at: null,
    proposals_end_at: null,
    proposals_per_user: 1,
    sponsors_per_proposal: 0,
    proposals_repeated: false,
    proposals_repeat_length: 1,
    proposals_starting_notifications: true,
    proposals_ending_notifications: true,
    recommendations_active: false,
    recommendations_start_at: null,
    recommendations_end_at: null,
    recommendations_per_user: 1,
    recommendations_repeated: false,
    recommendations_repeat_length: 1,
    recommendations_allow_negative: false,
    recommendations_starting_notifications: true,
    recommendations_ending_notifications: true,
    recommendations_text: 'Please write your recommendation below.',
    recommendations_all_label: 'All Proposals',
    recommendations_submitted_label: 'Submitted Proposals',
    recommendations_related_label: 'Related Proposals',
    recommendations_canned_responses: true,
    recommendations_character_limit: 500,
    voting_active: false,
    voting_starts_at: null,
    voting_ends_at: null,
    votes_per_user: 1,
    voting_starting_notifications: true,
    voting_ending_notifications: true,
    allow_multiple_categories: false,
}

export default function NewElectionDialog(props) {

    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();
    const history = useHistory();

    const initTime = DateTime.local().setZone(app.club.timezone).plus({days: 1}).startOf('hour');

    const { open, onClose, onClosed, copy, copyFields, copyOptions } = props;

    const [ groups, setGroups ] = useState(null);
    const [ errors, setErrors ] = useState({});
    const [ activeStep, setActiveStep ] = useState(0);
    const [ completedSteps, setCompletedSteps ] = useState({'-1': true});
    const [ loading, setLoading ] = useState(false);

    const [ election, setElection ] = useState({
        ...initElection,
        starts_at: initTime,
    });
    const [ fields, setFields ] = useState([]);

    // Getters ---------------------------------------------------

    const getGroups = () => {
        setLoading(true);

        api.get('/admin/group')
            .then(res => setGroups(res.groups))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (!groups) getGroups();

        if (copy) setElection(copy);

        if (copyFields && copyOptions?.copyFields) {
            setFields(copyFields);
        }
    }, [open]);

    // Handlers ---------------------------------------------------

    const handleElection = name => event => {

        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        if (isError(name)) delete errors[name];

        setElection({ ...election, [name]: value });
    };

    const handleNext = () => {
        const nextStep = activeStep + 1;
        if (nextStep > 6) {
            postNewElection();
        }
        else {
            const newCompleted = completedSteps;
            newCompleted[activeStep] = true;
            setCompletedSteps(newCompleted);
            setActiveStep(nextStep);
        }
    }

    const handleBack = () => {
        const previousStep = activeStep - 1;
        if (previousStep < 0) {
            handleClose()
        }
        else setActiveStep(previousStep);
    }

    const getButtonText = () => {
        if (activeStep === 6) return 'Finish';
        return 'Next';
    }

    // Post Function ------------------------------------------

    const postNewElection = () => {

        setLoading(true)

        const data = {...election, fields, copyOptions}

        if (election.starts_at) data.starts_at = election.starts_at.toSeconds();
        if (election.proposals_start_at) data.proposals_start_at = election.proposals_start_at.toSeconds();
        if (election.proposals_end_at) data.proposals_end_at = election.proposals_end_at.toSeconds();
        if (election.recommendations_start_at) data.recommendations_start_at = election.recommendations_start_at.toSeconds();
        if (election.recommendations_end_at) data.recommendations_end_at = election.recommendations_end_at.toSeconds();
        if (election.voting_starts_at) data.voting_starts_at = election.voting_starts_at.toSeconds();
        if (election.voting_ends_at) data.voting_ends_at = election.voting_ends_at.toSeconds();

        api.post('/admin/election/new', data)
            .then(res => history.push(`/admin/elections/${res.id}`))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                    setLoading(false)
                }
            })
            .finally(() => setLoading(false))
    }

    const handleClose = () => {
        onClose();
    }

    const handleClosed = () => {
        setElection({
            ...initElection,
            starts_at: initTime,
        });
        setFields([]);
        setActiveStep(0);

        if (onClosed) onClosed();
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const hasErrors = label => {
        let pagedFields = [];

        switch (label) {
            case 'Election':
                pagedFields = ['name', 'button_name', 'description', 'starts_at', 'repeated', 'repeat_period', 'repeat_amount', 'add_date_to_name'];
                break;
            case 'Proposals':
                pagedFields = ['proposals_active', 'proposals_start_at', 'proposals_end_at', 'proposals_per_user', 'proposals_repeated', 'proposals_repeat_length', 'proposals_starting_notifications', 'proposals_ending_notifications', 'sponsors_per_proposal'];
                break;
            case 'Recommendations':
                pagedFields = ['recommendations_active', 'recommendations_start_at', 'recommendations_end_at', 'recommendations_per_user', 'recommendations_repeated', 'recommendations_repeat_length', 'recommendations_starting_notification', 'recommendations_ending_notifications', 'recommendations_text', 'recommendations_canned_responses', 'recommendations_character_limit'];
                break;
            case 'Voting':
                pagedFields = ['voting_active', 'voting_start_at', 'voting_end_at', 'votes_per_user', 'voting_starting_notifications', 'voting_ending_notifications'];
                break;
        }

        const errorFields = pagedFields.filter(field => errors[field]);

        return errorFields.length;
    }

    const memoizedSteps = useMemo(() => {
        return (
            <Stepper activeStep={activeStep}>
                {steps.map((label, index) => {
                    const labelProps = {};

                    if (hasErrors(label) > 0) {
                        labelProps.error = true;
                    }

                    return (
                        <Step key={index}>
                            <StepButton
                                onClick={() => setActiveStep(index)}
                                completed={completedSteps[index]}
                                disabled={! completedSteps[index - 1]}
                                >
                                <StepLabel {...labelProps}>
                                    {label}
                                </StepLabel>
                            </StepButton>
                        </Step>
                    )
                })}
            </Stepper>
        )
    }, [activeStep, errors]);

    const getDateFormat = () => {
        switch (election.repeat_period) {
            case 'years':
                return 'y';
            case 'months':
                return 'LLLL y';
            case 'weeks':
            case 'days':
                return 'DDD';
            default: return '';
        }
    };

    const electionNameWithDate = useMemo(() => {
        if (election.repeated) {

            const dateFormat = getDateFormat();

            switch (election.add_date_to_name) {
                case 0: return election.name;
                case 1:
                    return election.starts_at.toFormat(dateFormat) + ' ' + election.name;
                case 2:
                    return election.name + ' ' + election.starts_at.toFormat(dateFormat);
            }
        } else {
            return election.name;
        }

    }, [election.add_date_to_name, election.repeated, election.name, election.starts_at, election.repeat_period]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionProps={{onExited: handleClosed}}
            fullScreen
            aria-labelledby="new-election"
            classes={{paperScrollPaper: classes.dialogPaper}}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="election-builder">
                <span>Election Builder</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            {memoizedSteps}
            <DialogContent dividers className={classes.content}>
                { (activeStep === 0) &&
                    <ElectionDetails
                        election={election}
                        handleElection={handleElection}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        electionNameWithDate={electionNameWithDate}
                        newElection
                    />
                }
                { (activeStep === 1) &&
                    <ProposalDetails
                        election={election}
                        handleElection={handleElection}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        groups={groups}
                        newElection
                    />
                }
                { (activeStep === 2) &&
                    <FormBuilder
                        fields={fields}
                        setFields={setFields}
                        errors={errors}
                        type='election'
                        election={election}
                        newForm={!copyFields}
                    />
                }
                { (activeStep === 3) &&
                    <RecommendationDetails
                        election={election}
                        handleElection={handleElection}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        groups={groups}
                        newElection
                    />
                }
                { (activeStep === 4) &&
                    <VotingDetails
                        election={election}
                        handleElection={handleElection}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        groups={groups}
                        newElection
                    />
                }
                { (activeStep === 5) &&
                    <OtherDetails
                        election={election}
                        handleElection={handleElection}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        groups={groups}
                        newElection
                    />
                }
                { (activeStep === 6) &&
                    <ElectionReview
                        election={election}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        groups={groups}
                        electionNameWithDate={electionNameWithDate}
                        newElection
                    />
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleBack}>
                    {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>
                <Button
                    onClick={handleNext}
                    variant="contained"
                    color='primary'
                    >
                    {getButtonText()}
                </Button>
           </DialogActions>
        </Dialog>
    )
}
